import {
  CameraOutlined,
  CheckCircleFilled,
  FilePdfOutlined,
  PictureOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Image, Progress } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { shrinkPhoto } from './shrink';
import {
  allowsPdf,
  isPdf,
  PDF_MAX_BYTES,
  type PhotoKind,
  PhotoUploadError,
  photoErrorMessage,
  uploadPhoto,
} from './upload';
import './photos.css';

/** What is shown: a picture, or a PDF (a link, browsers don't preview it inline). */
type Preview = { url: string; pdf: boolean; name?: string };

type State =
  | { step: 'empty' }
  | { step: 'working'; preview: Preview; progress: number; label: string }
  | { step: 'done'; preview: Preview }
  | {
      step: 'failed';
      preview: Preview | null;
      message: string;
      blob: Blob | null;
    };

/**
 * Take or pick a photo, shrink it, send it to arqam, and hand back its id
 * (`onChange`). Retry on failure, remove before saving. `currentUrl` shows a
 * photo already saved (it stays until a new one is sent). Receipts can also be
 * a PDF (arqam doc 49): sent as it is, up to 10 MB.
 */
export function PhotoField({
  kind,
  value,
  onChange,
  label,
  required = false,
  hint,
  currentUrl,
  currentContentType,
  onBusyChange,
  pasteFromClipboard = false,
}: {
  kind: PhotoKind;
  /** The id of the photo sent in this form, if any. */
  value: string | null;
  onChange: (photoId: string | null) => void;
  label: string;
  required?: boolean;
  hint?: string;
  /** A photo already saved with the record (signed link from the API). */
  currentUrl?: string | null;
  /** Its `Photo.contentType`, to show a PDF as a link. */
  currentContentType?: string | null;
  /** True while shrinking or sending: the form should wait. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Desk (doc 30): Ctrl+V anywhere on the page takes a copied picture (a
   * screenshot, an image copied from an email). One field per page.
   */
  pasteFromClipboard?: boolean;
}) {
  const allowPdf = allowsPdf(kind);
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ step: 'empty' });
  const [dragging, setDragging] = useState(false);
  const pickRef = useRef<(file: File | undefined) => void>(() => {});

  // The form cleared the photo (e.g. after saving): back to empty.
  useEffect(() => {
    if (value === null && state.step === 'done') setState({ step: 'empty' });
  }, [value, state.step]);

  useEffect(() => {
    onBusyChange?.(state.step === 'working');
  }, [state.step, onBusyChange]);

  async function send(blob: Blob, preview: Preview) {
    const label = preview.pdf ? 'إرسال الملف…' : 'إرسال الصورة…';
    setState({ step: 'working', preview, progress: 0, label });
    try {
      const photo = await uploadPhoto(blob, kind, (progress) =>
        setState({ step: 'working', preview, progress, label }),
      );
      setState({ step: 'done', preview });
      onChange(photo.id);
    } catch (error) {
      setState({
        step: 'failed',
        preview,
        blob,
        message: photoErrorMessage(error),
      });
      onChange(null);
    }
  }

  const fail = (code: string) => {
    setState({
      step: 'failed',
      preview: null,
      blob: null,
      message: photoErrorMessage(new PhotoUploadError(code, 0)),
    });
    onChange(null);
  };

  async function pick(file: File | undefined) {
    if (!file) return;
    if (isPdf(file)) {
      if (!allowPdf) return fail('PDF_NOT_ALLOWED');
      if (file.size > PDF_MAX_BYTES) return fail('PDF_TOO_LARGE');
      const pdf = new Blob([file], { type: 'application/pdf' });
      await send(pdf, {
        url: URL.createObjectURL(pdf),
        pdf: true,
        name: file.name,
      });
      return;
    }
    const original = URL.createObjectURL(file);
    setState({
      step: 'working',
      preview: { url: original, pdf: false },
      progress: 0,
      label: 'تجهيز الصورة…',
    });
    let blob: Blob;
    try {
      blob = await shrinkPhoto(file);
    } catch {
      fail('READ_FAILED');
      return;
    } finally {
      URL.revokeObjectURL(original);
    }
    await send(blob, { url: URL.createObjectURL(blob), pdf: false });
  }

  pickRef.current = pick;

  // A copied picture pasted anywhere: only files, typed text pastes as usual.
  useEffect(() => {
    if (!pasteFromClipboard) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.files ?? [])][0];
      if (!file) return;
      e.preventDefault();
      pickRef.current(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [pasteFromClipboard]);

  const current: Preview | null = currentUrl
    ? { url: currentUrl, pdf: currentContentType === 'application/pdf' }
    : null;
  const preview = state.step === 'empty' ? current : (state.preview ?? current);

  return (
    <fieldset
      className={`photo-field${required && !value && !currentUrl ? ' is-required' : ''}${dragging ? ' is-dragging' : ''}`}
      aria-label={label}
      // A file dropped from the computer (desk).
      onDragOver={(e) => {
        if (state.step === 'working') return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (state.step !== 'working') pick(e.dataTransfer.files[0]);
      }}
    >
      <div className="photo-label">
        {label}
        {required && <span className="photo-required"> (إلزامية)</span>}
      </div>
      {hint && <div className="field-hint">{hint}</div>}

      {preview && <PreviewBox preview={preview} alt={label} />}

      {state.step === 'working' && (
        <div className="photo-status">
          {state.label}
          <Progress percent={Math.round(state.progress * 100)} />
        </div>
      )}
      {state.step === 'done' && (
        <div className="photo-status is-done">
          <CheckCircleFilled />{' '}
          {state.preview.pdf ? 'أُرسل الملف' : 'أُرسلت الصورة'}
        </div>
      )}
      {state.step === 'failed' && (
        <div className="photo-status is-failed">
          {state.message}
          {state.blob && state.preview && (
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                state.blob && state.preview && send(state.blob, state.preview)
              }
            >
              إعادة المحاولة
            </Button>
          )}
        </div>
      )}

      {state.step !== 'working' && (
        <div className="photo-buttons">
          <Button
            size="large"
            type={!preview ? 'primary' : 'default'}
            icon={<CameraOutlined />}
            onClick={() => camera.current?.click()}
          >
            {preview ? 'صورة أخرى' : 'التقاط صورة'}
          </Button>
          <Button
            size="large"
            icon={<PictureOutlined />}
            onClick={() => gallery.current?.click()}
          >
            {allowPdf ? 'من المعرض أو PDF' : 'من المعرض'}
          </Button>
          {state.step === 'done' && (
            <Button
              size="large"
              danger
              onClick={() => {
                setState({ step: 'empty' });
                onChange(null);
              }}
            >
              إزالة
            </Button>
          )}
        </div>
      )}

      {/* capture: opens the back camera directly on phones */}
      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={gallery}
        type="file"
        accept={allowPdf ? 'image/*,application/pdf' : 'image/*'}
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </fieldset>
  );
}

function PreviewBox({ preview, alt }: { preview: Preview; alt: string }) {
  if (preview.pdf) return <PdfLink url={preview.url} name={preview.name} />;
  return (
    <Image
      src={preview.url}
      alt={alt}
      className="photo-preview"
      preview={{ mask: 'تكبير' }}
    />
  );
}

/** A PDF receipt: opens in the browser's viewer. */
function PdfLink({ url, name }: { url: string; name?: string }) {
  return (
    <a className="photo-pdf" href={url} target="_blank" rel="noreferrer">
      <FilePdfOutlined />
      <span>
        <span className="photo-pdf-title">فتح الملف (PDF)</span>
        {name && <span className="field-hint ltr-num">{name}</span>}
      </span>
    </a>
  );
}

/** A saved photo: tap to see it full screen (a PDF: tap to open it). */
export function PhotoView({
  url,
  caption,
  alt,
  contentType,
}: {
  url: string;
  caption?: string;
  alt: string;
  contentType?: string | null;
}) {
  return (
    <figure className="photo-view">
      {contentType === 'application/pdf' ? (
        <PdfLink url={url} />
      ) : (
        <Image
          src={url}
          alt={alt}
          className="photo-preview"
          preview={{ mask: 'تكبير' }}
        />
      )}
      {caption && <figcaption className="field-hint">{caption}</figcaption>}
    </figure>
  );
}
